import React, { useState, useRef } from 'react';
import { useData } from '../context/DataContext';
import {
  ArrowRight,
  Search,
  Layout,
  Target,
  GitMerge,
  Award,
  Settings,
  TrendingUp,
  Mail,
  Phone,
  Linkedin,
  ChevronDown,
  Star,
  CheckCircle2,
  Building2,
  Users,
  BarChart3,
  Menu,
  X,
  Play,
  Instagram,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription as DialogDesc } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

import ConsultationPopup from '../components/ConsultationPopup';
import { getCaptchaToken, verifyCaptcha } from '../utils/captcha';
import { submitForm } from '../utils/api';

const HomePage = () => {
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [showIBDropdown, setShowIBDropdown] = useState(false);
  const [showInsightsDropdown, setShowInsightsDropdown] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', company: '', city: '', lookingFor: '', message: ''
  });
  const [newsletterForm, setNewsletterForm] = useState({ email: '', segment: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const teamFirstRowRef = useRef(null);
  const teamSecondRowRef = useRef(null);
  const testimonialCarouselRef = useRef(null);
  const videoCarouselRef = useRef(null);
  const dropdownRef = useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowServicesDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, loading } = useData();

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  // Map backend data to existing component variables
  const {
    heroData,
    statsData: stats,
    servicesData: services,
    frameworkData,
    whyChooseUsData: whyChooseUs,
    teamData,
    testimonialsData: textTestimonials,
    videoTestimonials,
    faqData,
  } = data;

  const frameworkSteps = frameworkData.steps.map(step => ({
    ...step,
    icon: {
      Search: Search,
      Layout: Layout,
      Target: Target,
      GitMerge: GitMerge
    }[step.icon] || Search
  }));

  const whyChooseUsMapped = whyChooseUs.map(item => ({
    ...item,
    icon: {
      Award: Award,
      Settings: Settings,
      TrendingUp: TrendingUp
    }[item.icon] || Award
  }));

  const teamFirstRow = teamData.filter(m => Number(m.row) === 1 || (!m.row && teamData.indexOf(m) < 3));
  const teamSecondRow = teamData.filter(m => Number(m.row) === 2 || (!m.row && teamData.indexOf(m) >= 3));


  const scrollCarousel = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = ref.current.clientWidth;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Form submission handlers with Formspree and EmailJS placeholders
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('contact_form');
      const captchaResult = await verifyCaptcha(token, 'contact_form');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      await submitForm('contact', contactForm);
      toast.success('Thank you! We\'ll be in touch within 24–48 working hours.');
      setContactForm({ name: '', email: '', phone: '', company: '', city: '', lookingFor: '', message: '' });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please email us directly at reachus@samvedanam.com');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('newsletter_form');
      const captchaResult = await verifyCaptcha(token, 'newsletter_form');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        return;
      }

      // [FORMSPREE ENDPOINT] Replace with: https://formspree.io/f/YOUR_NEWSLETTER_FORM_ID
      console.log('Newsletter subscription:', newsletterForm);
      toast.success('You\'re subscribed! Expect curated insights from the Samvedanam team.');
      setNewsletterForm({ email: '', segment: '' });
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <ConsultationPopup isOpen={isConsultationOpen} onClose={() => setIsConsultationOpen(false)} />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-slate-950/95 z-10"></div>
          <img
            src="/images/stock/hero_bg.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="container mx-auto px-4 z-20 relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20 px-4 py-2 text-sm">
              {heroData.badge}
            </Badge>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              {heroData.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
              {heroData.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => setIsConsultationOpen(true)}
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg group"
              >
                {heroData.ctaText}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              {/* Our Services Dropdown Button */}
              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={() => setShowServicesDropdown(true)}
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-amber-500 hover:border-amber-500 px-8 py-6 text-lg group"
                  onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                >
                  {heroData.secondaryCtaText}
                  <ChevronDown className="ml-2 h-5 w-5 group-hover:rotate-180 transition-transform" />
                </Button>

                {/* Services Dropdown */}
                {showServicesDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl p-2 min-w-[320px] z-50">
                    {services.map((option, index) => (
                      <a
                        key={index}
                        href={option.href}
                        className="block p-4 hover:bg-slate-700 rounded-lg transition-colors text-left"
                        onClick={() => setShowServicesDropdown(false)}
                      >
                        <div className="text-white font-semibold mb-1">{option.title}</div>
                        <div className="text-slate-400 text-sm">{option.description}</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 hover:bg-slate-800/50 transition-all">
                <div className="text-4xl md:text-5xl font-bold text-amber-500 mb-2">{stat.number}</div>
                <div className="text-slate-400 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capital Solutions for Every Growth Stage */}
      <section id="services" className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">Our Services</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Solutions for Every Growth Stage
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Comprehensive financial advisory services tailored to your growth stage
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all group flex flex-col h-full">
                <CardHeader>
                  <div className="h-48 rounded-lg overflow-hidden mb-4">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {service.phases.map((phase, idx) => (
                      <Badge key={idx} className="bg-slate-700 text-slate-300">{phase}</Badge>
                    ))}
                  </div>
                  <CardTitle className="text-2xl text-white mb-3">{service.title}</CardTitle>
                  <CardDescription className="text-slate-300 leading-relaxed mb-4">
                    {service.description}
                  </CardDescription>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.segments.map((segment, idx) => (
                      <Badge key={idx} variant="outline" className="border-amber-500/30 text-amber-500 text-xs">
                        {segment}
                      </Badge>
                    ))}
                  </div>
                </CardHeader>
                <CardContent className="mt-auto">
                  <a href={service.href}>
                    <Button className="w-full bg-slate-700 hover:bg-slate-600 text-white group">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section id="our-approach" className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">{frameworkData.subtitle}</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {frameworkData.title}
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="prose prose-lg prose-invert text-left">
              <p className="text-slate-300 leading-relaxed mb-6 text-lg">
                {frameworkData.description}
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[400px]">
              <img
                src="/images/stock/boardroom.jpg"
                alt="Consulting Team"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between gap-6 relative mt-16 pb-4">
            {/* Horizontal connection line for desktop */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-slate-700" />

            {frameworkSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="flex-1 relative group w-full">
                  <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 h-full hover:bg-slate-800 transition-colors relative z-10 block">
                    <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                      <IconComponent className="h-8 w-8 text-amber-500" />
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-amber-500 mb-2">STEP {step.number}</div>
                      <h3 className="text-xl font-bold text-white mb-3 break-words">{step.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">Why SAMVEDANAM</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChooseUsMapped.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all group overflow-hidden">
                  <div className="h-64 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white mb-3">{item.title}</CardTitle>
                        <CardDescription className="text-slate-400 text-base leading-relaxed">
                          {item.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="our-team" className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">Our Team</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Meet Our Experts
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Tier 1 industry professionals with decades of combined experience
            </p>
          </div>

          {/* First Row - Carousel */}
          {teamFirstRow.length > 0 && (
            <div className="relative mb-12">
              <button
                onClick={() => scrollCarousel(teamFirstRowRef, 'left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>

              <div
                ref={teamFirstRowRef}
                className="flex gap-8 overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {teamFirstRow.map((member, index) => (
                  <Card key={member.id || index} className="bg-slate-800/50 border-slate-700 backdrop-blur-sm transition-all group flex-shrink-0 w-full md:w-[calc(33.333%-1.5rem)] relative overflow-hidden flex flex-col h-full">
                    <CardHeader className="pb-2">
                      <div className="relative mb-4">
                        <div className="w-full h-72 rounded-lg overflow-hidden">
                          <img
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        {member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center transition-colors shadow-lg"
                          >
                            <Linkedin className="h-5 w-5 text-white" />
                          </a>
                        )}
                      </div>
                      <CardTitle className="text-xl text-white mb-1">{member.name}</CardTitle>
                      <CardDescription className="text-amber-500 font-medium">{member.role}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col pt-0">
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 mb-4">{member.bio}</p>

                      <div className="mt-auto">
                        <Dialog>
                          <DialogTrigger asChild>
                            <button className="text-amber-500 hover:text-amber-400 text-sm font-semibold flex items-center group/btn transition-colors">
                              Read more <ArrowRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="bg-slate-900 border border-slate-700 text-white sm:max-w-[500px]">
                            <DialogHeader>
                              <div className="flex items-start gap-5">
                                <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0 border border-slate-700 p-1">
                                  <img src={member.image} alt={member.name} className="w-full h-full object-contain rounded-md" />
                                </div>
                                <div className="text-left">
                                  <DialogTitle className="text-2xl text-white mb-1">{member.name}</DialogTitle>
                                  <DialogDesc className="text-amber-500 font-medium text-base mb-3">{member.role}</DialogDesc>
                                  {member.linkedin && (
                                    <a
                                      href={member.linkedin}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex w-8 h-8 rounded-full bg-amber-600 hover:bg-amber-700 items-center justify-center transition-colors shadow-lg"
                                    >
                                      <Linkedin className="h-4 w-4 text-white" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </DialogHeader>
                            <div className="mt-4 text-slate-300 leading-relaxed text-sm whitespace-pre-line max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                              {member.bio}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <button
                onClick={() => scrollCarousel(teamFirstRowRef, 'right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronRight className="h-6 w-6 text-white" />
              </button>
            </div>
          )}

          {/* Second Row - Carousel */}
          {teamSecondRow.length > 0 && (
            <div className="relative">
              <button
                onClick={() => scrollCarousel(teamSecondRowRef, 'left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>

              <div
                ref={teamSecondRowRef}
                className="flex gap-8 overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {teamSecondRow.map((member, index) => (
                  <Card key={member.id || index} className="bg-slate-800/50 border-slate-700 backdrop-blur-sm transition-all group flex-shrink-0 w-full md:w-[calc(33.333%-1.5rem)] relative overflow-hidden flex flex-col h-full">
                    <CardHeader className="pb-2">
                      <div className="relative mb-4">
                        <div className="w-full h-72 rounded-lg overflow-hidden">
                          <img
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        {member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center transition-colors shadow-lg"
                          >
                            <Linkedin className="h-5 w-5 text-white" />
                          </a>
                        )}
                      </div>
                      <CardTitle className="text-xl text-white mb-1">{member.name}</CardTitle>
                      <CardDescription className="text-amber-500 font-medium">{member.role}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col pt-0">
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 mb-4">{member.bio}</p>

                      <div className="mt-auto">
                        <Dialog>
                          <DialogTrigger asChild>
                            <button className="text-amber-500 hover:text-amber-400 text-sm font-semibold flex items-center group/btn transition-colors">
                              Read more <ArrowRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                          </DialogTrigger>
                          <DialogContent className="bg-slate-900 border border-slate-700 text-white sm:max-w-[500px]">
                            <DialogHeader>
                              <div className="flex items-start gap-5">
                                <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0 border border-slate-700 p-1">
                                  <img src={member.image} alt={member.name} className="w-full h-full object-contain rounded-md" />
                                </div>
                                <div className="text-left">
                                  <DialogTitle className="text-2xl text-white mb-1">{member.name}</DialogTitle>
                                  <DialogDesc className="text-amber-500 font-medium text-base mb-3">{member.role}</DialogDesc>
                                  {member.linkedin && (
                                    <a
                                      href={member.linkedin}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex w-8 h-8 rounded-full bg-amber-600 hover:bg-amber-700 items-center justify-center transition-colors shadow-lg"
                                    >
                                      <Linkedin className="h-4 w-4 text-white" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </DialogHeader>
                            <div className="mt-4 text-slate-300 leading-relaxed text-sm whitespace-pre-line max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                              {member.bio}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <button
                onClick={() => scrollCarousel(teamSecondRowRef, 'right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronRight className="h-6 w-6 text-white" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Client Speak - Testimonials */}
      <section id="client-speak" className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">Client Success</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Trusted by founders and business leaders across industries
            </p>
          </div>

          {/* Row 1: Text Testimonials Carousel */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Client Testimonials</h3>
            <div className="relative max-w-6xl mx-auto">
              <button
                onClick={() => scrollCarousel(testimonialCarouselRef, 'left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>

              <div
                ref={testimonialCarouselRef}
                className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {textTestimonials.map((testimonial, index) => (
                  <Card key={index} className="bg-slate-800/50 border-slate-700 backdrop-blur-sm hover:bg-slate-800/70 transition-all flex-shrink-0 w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]">
                    <CardHeader>
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-amber-500 text-amber-500" />
                        ))}
                      </div>
                      <CardDescription className="text-slate-300 text-base leading-relaxed max-h-[150px] overflow-y-auto pr-2">
                        "{testimonial.content}"
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">{testimonial.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{testimonial.name}</p>
                          <p className="text-slate-400 text-sm">{testimonial.role}, {testimonial.company}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <button
                onClick={() => scrollCarousel(testimonialCarouselRef, 'right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          {/* Row 2: Video Testimonials */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Video Testimonials</h3>
            <div className="relative max-w-6xl mx-auto">
              <button
                onClick={() => scrollCarousel(videoCarouselRef, 'left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>

              <div
                ref={videoCarouselRef}
                className="flex overflow-x-auto gap-6 scrollbar-hide pb-4 scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {videoTestimonials.map((video, index) => (
                  <Card
                    key={index}
                    className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all cursor-pointer group min-w-[300px] md:min-w-[400px] flex-shrink-0"
                    onClick={() => setSelectedVideo(video.youtubeId)}
                  >
                    <CardContent className="p-0">
                      <div className="relative h-48 md:h-64 overflow-hidden rounded-t-lg">
                        <img
                          src={video.thumbnail}
                          alt={video.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                          <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                            <Play className="h-8 w-8 text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <p className="text-white font-semibold mb-1 text-lg">{video.name}</p>
                        <p className="text-slate-400 text-sm">{video.title}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <button
                onClick={() => scrollCarousel(videoCarouselRef, 'right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-amber-600 hover:bg-amber-700 flex items-center justify-center shadow-xl"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4">
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-4 right-4 text-white hover:text-amber-500 z-10"
          >
            <X className="h-8 w-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-xl overflow-hidden bg-black/50 shadow-2xl">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">FAQ</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Everything you need to know about working with us
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 items-start">
            {/* Column 1 */}
            <div className="space-y-4">
              {faqData.slice(0, Math.ceil(faqData.length / 2)).map((faq, index) => (
                <Accordion key={index} type="single" collapsible>
                  <AccordionItem
                    value={`item-${index}`}
                    className="bg-slate-800/40 border-slate-700/50 rounded-xl px-6 backdrop-blur-sm border transition-all hover:border-amber-500/30 min-h-[100px] flex flex-col justify-center"
                  >
                    <AccordionTrigger className="text-white hover:text-amber-500 text-left items-start py-5 font-semibold leading-relaxed h-full">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-400 leading-relaxed pb-6 text-base">
                      {faq.answer.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
            {/* Column 2 */}
            <div className="space-y-4">
              {faqData.slice(Math.ceil(faqData.length / 2)).map((faq, index) => (
                <Accordion key={index} type="single" collapsible>
                  <AccordionItem
                    value={`item-${index}`}
                    className="bg-slate-800/40 border-slate-700/50 rounded-xl px-6 backdrop-blur-sm border transition-all hover:border-amber-500/30 min-h-[100px] flex flex-col justify-center"
                  >
                    <AccordionTrigger className="text-white hover:text-amber-500 text-left items-start py-5 font-semibold leading-relaxed h-full">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-400 leading-relaxed pb-6 text-base">
                      {faq.answer.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Get in Touch Section */}
      <section id="get-in-touch" className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 bg-amber-500/10 text-amber-500 border-amber-500/20">Get In Touch</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Let's Start a Conversation
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Ready to take your business to the next level? Reach out to us today.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Part A: Contact Form */}
            <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Send Us a Message</CardTitle>
                <CardDescription className="text-slate-400">
                  Fill out the form and we'll get back to you within 24–48 working hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <Input
                    placeholder="Full Name *"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <Input
                    placeholder="Company / Startup Name"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <Input
                    placeholder="City"
                    value={contactForm.city}
                    onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <select
                    value={contactForm.lookingFor}
                    onChange={(e) => setContactForm({ ...contactForm, lookingFor: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white"
                  >
                    <option value="">What are you looking for? *</option>
                    <option value="fundraising">Fundraising</option>
                    <option value="ma">Mergers & Acquisitions</option>
                    <option value="advisory">Business Advisory</option>
                    <option value="academy">Academy & Workshops</option>
                    <option value="other">Other</option>
                  </select>
                  <Textarea
                    placeholder="Briefly describe your requirement"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows={4}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {/* Part B: Contact Information */}
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-white">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <a href="mailto:reachus@samvedanam.com" className="flex items-start gap-4 hover:bg-slate-700/50 p-3 rounded-lg transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Email</p>
                      <p className="text-white">reachus@samvedanam.com</p>
                    </div>
                  </a>
                  <a href="tel:+919741306897" className="flex items-start gap-4 hover:bg-slate-700/50 p-3 rounded-lg transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Phone</p>
                      <p className="text-white">+91 97413 06897</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4 p-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Locations</p>
                      <p className="text-white">Chandigarh · Delhi · Bangalore</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Part C: Newsletter Signup */}
              <Card className="bg-gradient-to-br from-amber-600 to-orange-600 border-0 hidden">
                <CardHeader>
                  <CardTitle className="text-2xl text-white">Subscribe to Our Newsletter</CardTitle>
                  <CardDescription className="text-amber-50">
                    Get the latest insights on investment banking and market trends
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={newsletterForm.email}
                      onChange={(e) => setNewsletterForm({ ...newsletterForm, email: e.target.value })}
                      required
                      className="bg-white/20 border-white/30 text-white placeholder:text-white/70"
                    />
                    <select
                      value={newsletterForm.segment}
                      onChange={(e) => setNewsletterForm({ ...newsletterForm, segment: e.target.value })}
                      className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-md text-white"
                    >
                      <option value="">I am a...</option>
                      <option value="founder">Founder</option>
                      <option value="cxo">CXO</option>
                      <option value="investor">Investor</option>
                      <option value="other">Other</option>
                    </select>
                    <Button type="submit" className="w-full bg-white text-amber-600 hover:bg-amber-50">
                      Subscribe
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
